import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { HalfDonutChartProps } from './half-donut-chart.types';
import { formatCompact, resolveCssVar } from './donut-chart.utils';

function buildOption(value: number, accentColor: string, label: string): EChartsOption {
  return {
    series: [
      {
        type: 'pie',
        radius: ['70%', '85%'],
        center: ['50%', '75%'],
        startAngle: 180,
        endAngle: 360,
        silent: true,
        animation: true,
        animationDuration: 900,
        animationEasing: 'cubicOut',
        emphasis: { disabled: true },
        itemStyle: { borderRadius: 6 },
        label: { show: false },
        labelLine: { show: false },
        data: [{ value: 1, name: label, itemStyle: { color: accentColor } }],
      },
    ],
    graphic: [
      {
        type: 'text' as const,
        left: 'center',
        top: '55%',
        style: {
          text: formatCompact(value),
          fontSize: 40,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500 as const,
          fill: accentColor,
          textAlign: 'center',
          textVerticalAlign: 'middle',
        } as any,
        z: 10,
      },
    ],
  };
}

export function HalfDonutChart({ value, label = 'Активные', redirectBase }: HalfDonutChartProps) {
  const [accentColor, setAccentColor] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ReactECharts>(null);

  const resolve = useCallback(
    () => setAccentColor(resolveCssVar('--rd-chart-1', '#D32D2F')),
    [],
  );

  useEffect(() => {
    resolve();
    const observer = new MutationObserver(resolve);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, [resolve]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      chartRef.current?.getEchartsInstance().resize();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleClick = useCallback(() => {
    if (redirectBase) window.location.href = redirectBase;
  }, [redirectBase]);

  const option = useMemo(
    () => (accentColor ? buildOption(value, accentColor, label) : {}),
    [value, accentColor, label],
  );

  if (!accentColor) return null;

  return (
    <div className="half-donut-chart" ref={containerRef}>
      <ReactECharts
        ref={chartRef}
        option={option}
        onEvents={redirectBase ? { click: handleClick } : undefined}
        style={{ height: 280, width: '100%', cursor: redirectBase ? 'pointer' : 'default' }}
        opts={{ renderer: 'svg' }}
      />
    </div>
  );
}
